import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch } from '../store';
import {
  dismissBannerModal,
  expireBanner,
  fetchBanner,
  openBannerModal,
  selectActiveBanner,
  selectBannerModalOpen,
} from '../store/bannerSlice';
import { selectIsSocketConnected } from '../store/socketSlice';
import { BANNER_SEVERITY_LABELS, isBannerLive } from '../types/banner';
import './SiteBanner.css';

const SiteBanner: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const banner = useSelector(selectActiveBanner);
  const modalOpen = useSelector(selectBannerModalOpen);
  const socketConnected = useSelector(selectIsSocketConnected);
  const barRef = useRef<HTMLButtonElement>(null);
  const live = banner && isBannerLive(banner) ? banner : null;

  useEffect(() => {
    if (!banner) return;

    const expireIfNeeded = () => {
      if (!isBannerLive(banner)) {
        dispatch(expireBanner());
      }
    };

    expireIfNeeded();

    const endsAt = Date.parse(banner.endsAt);
    const untilEnd = Number.isNaN(endsAt) ? 0 : endsAt - Date.now();
    const timeoutId =
      untilEnd > 0
        ? window.setTimeout(expireIfNeeded, Math.min(untilEnd, 2_147_000_000))
        : undefined;
    const intervalId = window.setInterval(expireIfNeeded, 45_000);

    return () => {
      if (timeoutId != null) window.clearTimeout(timeoutId);
      window.clearInterval(intervalId);
    };
  }, [banner, dispatch]);

  // Гостевые страницы без сокета: периодически перечитываем HTTP-снимок,
  // чтобы увидеть объявление, которое админ включил, пока вкладка уже открыта.
  useEffect(() => {
    if (socketConnected) return;
    const id = window.setInterval(() => {
      void dispatch(fetchBanner());
    }, 45_000);
    return () => window.clearInterval(id);
  }, [socketConnected, dispatch]);

  useEffect(() => {
    if (!modalOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') dispatch(dismissBannerModal());
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modalOpen, dispatch]);

  // Полоса сдвигает шапку и сайдбар на свою реальную высоту.
  useEffect(() => {
    const root = document.documentElement;
    const el = barRef.current;
    if (!live || !el) {
      root.classList.remove('has-site-banner');
      root.style.removeProperty('--site-banner-offset');
      return;
    }

    const apply = () => {
      root.classList.add('has-site-banner');
      root.style.setProperty('--site-banner-offset', `${Math.ceil(el.getBoundingClientRect().height)}px`);
    };
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(el);
    return () => {
      observer.disconnect();
      root.classList.remove('has-site-banner');
      root.style.removeProperty('--site-banner-offset');
    };
  }, [live]);

  if (!live) return null;

  const severity = live.severity || 'info';

  return (
    <>
      <button
        ref={barRef}
        type="button"
        className={`site-banner-bar site-banner-bar--${severity}`}
        onClick={() => dispatch(openBannerModal())}
        aria-label={`${live.title}. Открыть объявление`}
      >
        <span className="site-banner-bar__mark" aria-hidden>
          {severity === 'critical' ? '!' : 'i'}
        </span>
        <span className="site-banner-bar__text">
          <strong className="site-banner-bar__title">{live.title}</strong>
          <span className="site-banner-bar__body">{live.body}</span>
        </span>
        <span className="site-banner-bar__more">Подробнее</span>
      </button>

      {modalOpen && (
        <div
          className="site-banner-overlay"
          role="presentation"
          onClick={() => dispatch(dismissBannerModal())}
        >
          <div
            className={`site-banner-modal site-banner-modal--${severity}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="site-banner-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="site-banner-modal__close"
              onClick={() => dispatch(dismissBannerModal())}
              aria-label="Закрыть объявление"
            >
              ×
            </button>
            <div className="site-banner-modal__badge">
              {BANNER_SEVERITY_LABELS[severity]}
            </div>
            <h2 id="site-banner-title" className="site-banner-modal__title">
              {live.title}
            </h2>
            <p className="site-banner-modal__body">{live.body}</p>
            <div className="site-banner-modal__actions">
              <button
                type="button"
                className="site-banner-modal__ok"
                onClick={() => dispatch(dismissBannerModal())}
              >
                Понятно
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SiteBanner;
